// src/components/HrTeamTable.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../axiosInstance';

/** Hilfsfunktionen zur robusten Normalisierung */
function asArr(x) {
  return Array.isArray(x) ? x : [];
}

/** Namen robust zusammenbauen (supports firstName/lastName UND first_name/last_name) */
function joinName(item = {}) {
  const first = item.first_name ?? item.firstName ?? '';
  const last  = item.last_name  ?? item.lastName  ?? '';
  const full  = `${first} ${last}`.trim();
  return full || '-';
}

/** Datum hübsch formatiert (DE), akzeptiert ISO/Date/null */
function fmtDateDE(v) {
  if (!v) return '';
  try {
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE');
  } catch {
    return '';
  }
}

/**
 * Erwartete Formen:
 *  - Array direkt: [ ... ]
 *  - { list: [...] } / { items: [...] } / { data: [...] }
 *  - { users: [...], invites: [...] } o.ä. Mischformen
 * Wir vereinheitlichen auf ein Array von Einträgen mit:
 *  id/email/role/status/first_name/last_name/registered_at/invited_at/valid_until
 */
function normalizeHrTeamResponse(data) {
  // 1) Direktes Array (z.B. unsere /auth/api/hr-team/list Antwort) – unverändert durchreichen
  if (Array.isArray(data)) return data;

  // 2) Einfache Container
  const list =
    asArr(data?.list) ||
    asArr(data?.items) ||
    asArr(data?.data) ||
    [];

  if (list.length) return list;

  // 3) Users + Invites zusammenführen
  const users =
    asArr(data?.users) ||
    asArr(data?.hr_users) ||
    asArr(data?.team) ||
    [];

  const invites =
    asArr(data?.invites) ||
    asArr(data?.pendingInvites) ||
    asArr(data?.invitations) ||
    [];

  const normalizedUsers = users.map(u => ({
    id: u.id ?? u.user_id ?? null,
    email: u.email,
    role: u.role,
    status: 'registriert',
    first_name: u.first_name ?? u.firstName ?? null,
    last_name: u.last_name ?? u.lastName ?? null,
    registered_at: u.registered_at ?? u.created_at ?? null,
  }));

  const normalizedInvites = invites.map(i => {
    const validUntil = i.valid_until ?? i.expires_at ?? null;
    const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
    return {
      invite_id: i.invite_id ?? i.id ?? null,
      email: i.email,
      role: i.role ?? 'hr_user',
      status: isExpired ? 'abgelaufen' : 'eingeladen',
      invited_at: i.invited_at ?? i.created_at ?? null,
      valid_until: validUntil,
    };
  });

  return [...normalizedUsers, ...normalizedInvites];
}

function HrTeamTable({ refreshKey }) {
  const { t } = useTranslation();
  const [teamList, setTeamList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  // Übersetzungen für Status (Fallbacks, falls Keys fehlen)
  const statusLabels = {
    registriert: t('hrTeamTable.status.registered', 'Registriert'),
    eingeladen: t('hrTeamTable.status.invited', 'Eingeladen'),
    abgelaufen: t('hrTeamTable.status.expired', 'Abgelaufen'),
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        // wichtig: authApi -> https://app.menvio.ai/auth/...
        const res = await authApi.get('/api/hr-team/list', { withCredentials: true });
        const normalized = normalizeHrTeamResponse(res?.data);
        if (mounted) setTeamList(normalized);
      } catch (_err) {
        if (mounted) setTeamList([]); // robust: kein Crash
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  async function handleDelete(item) {
    const text =
      item.status === 'registriert'
        ? t('hrTeamTable.confirmDeleteRegistered', {
            firstName: item.first_name,
            lastName: item.last_name,
          })
        : t('hrTeamTable.confirmDeleteInvite', { email: item.email });

    if (!window.confirm(text)) return;

    const url =
      item.status === 'registriert'
        ? `/api/hr-team/user/${item.id}`
        : `/api/hr-team/invite/${item.invite_id}`;

    try {
      await authApi.delete(url, { withCredentials: true });
      setActionMsg(t('hrTeamTable.deleteSuccess', 'Gelöscht.'));
      setTeamList(list =>
        list.filter(x =>
          item.status === 'registriert' ? x.id !== item.id : x.invite_id !== item.invite_id
        )
      );
    } catch {
      setActionMsg(t('hrTeamTable.deleteError', 'Löschen fehlgeschlagen.'));
    }
  }

  async function handleResend(invite) {
    try {
      await authApi.post(
        `/api/hr-team/invite/resend/${invite.invite_id}`,
        {},
        { withCredentials: true }
      );
      setActionMsg(t('hrTeamTable.resendSuccess', 'Einladung erneut gesendet.'));
    } catch {
      setActionMsg(t('hrTeamTable.resendError', 'Erneutes Senden fehlgeschlagen.'));
    }
  }

  if (loading) return <div className="mt-4">{t('hrTeamTable.loading', 'Lade Team…')}</div>;
  if (!Array.isArray(teamList) || teamList.length === 0) {
    return <div className="mt-4">{t('hrTeamTable.noneFound', 'Keine Einträge gefunden.')}</div>;
  }

  return (
    <div className="bg-gray-900 rounded-2xl p-6 mt-10 shadow-lg border border-gray-700">
      <h3 className="font-bold mb-6 text-xl text-white flex items-center gap-2">
        <span className="inline-block w-2 h-6 bg-green-400 rounded-r-lg mr-2"></span>
        {t('hrTeamTable.heading', 'HR-Team')}
      </h3>

      {actionMsg && <div className="mb-4 text-green-400 font-medium">{actionMsg}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 bg-gray-800">
              <th className="py-3 px-4 rounded-tl-lg">{t('hrTeamTable.name', 'Name')}</th>
              <th className="py-3 px-4">{t('hrTeamTable.email', 'E-Mail')}</th>
              <th className="py-3 px-4">{t('hrTeamTable.role', 'Rolle')}</th>
              <th className="py-3 px-4">{t('hrTeamTable.status.label', 'Status')}</th>
              <th className="py-3 px-4">{t('hrTeamTable.date', 'Datum')}</th>
              <th className="py-3 px-4 rounded-tr-lg">{t('hrTeamTable.actions', 'Aktionen')}</th>
            </tr>
          </thead>
          <tbody>
            {teamList.map(item => (
              <tr
                key={item.id ?? item.invite_id}
                className="border-b border-gray-800 hover:bg-gray-800/60 transition"
              >
                {/* Name */}
                <td className="py-2 px-4 font-medium text-white">
                  {joinName(item)}
                </td>

                {/* E-Mail */}
                <td className="py-2 px-4 text-gray-300">{item.email}</td>

                {/* Rolle */}
                <td className="py-2 px-4">
                  {t(`hrTeamTable.roleOptions.${item.role}`, item.role)}
                </td>

                {/* Status-Pill */}
                <td className="py-2 px-4">
                  <span
                    className={`px-2 py-1 rounded-lg font-semibold text-xs ${
                      item.status === 'registriert'
                        ? 'bg-green-400 text-gray-900'
                        : item.status === 'eingeladen'
                        ? 'bg-yellow-300 text-gray-900'
                        : item.status === 'abgelaufen'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-700 text-gray-200'
                    }`}
                  >
                    {statusLabels[item.status] || item.status || '-'}
                  </span>
                </td>

                {/* Datum */}
                <td className="py-2 px-4 text-gray-400">
                  {item.status === 'registriert'
                    ? fmtDateDE(item.addedAt ?? item.registered_at ?? item.created_at)
                    : item.status === 'eingeladen'
                    ? fmtDateDE(item.invitedAt ?? item.invited_at)
                    : item.status === 'abgelaufen'
                    ? fmtDateDE(item.valid_until)
                    : ''}
                </td>

                {/* Aktionen */}
                <td className="py-2 px-4">
                  <button
                    onClick={() => handleDelete(item)}
                    className="text-red-400 hover:text-red-300 font-semibold mr-4 underline"
                  >
                    {t('hrTeamTable.delete', 'Löschen')}
                  </button>
                  {item.status !== 'registriert' && (
                    <button
                      onClick={() => handleResend(item)}
                      className="text-yellow-300 hover:text-yellow-200 font-semibold underline"
                      disabled={item.status === 'abgelaufen'}
                    >
                      {t('hrTeamTable.resend', 'Erneut senden')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default HrTeamTable;
